/** Light/dark theme: persisted in localStorage, applied as
 *  `data-theme` on <html> so styles.css token overrides take effect.
 *  Default follows the OS preference.
 *
 *  Storage access is guarded: browsers throw on it when site data is blocked
 *  (private modes, sandboxed frames). `getTheme` runs at boot in main.tsx,
 *  before any ErrorBoundary exists, so a throw there would white-screen every
 *  route — a blocked store just means "use the OS preference, don't persist". */

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function getTheme(): Theme {
  const stored = readStored()
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Blocked or full storage: the choice applies now but won't survive a reload.
  }
  applyTheme(theme)
}
